import { Empty, LocalizedDateTimeFromUtc, OverlayedLoader, TooltippedIcon } from '@mews-commander/ui';
import { LocalizedEnum, LocalizedText, lt } from '@mews/framework';
import { IconName, Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { isEmptyRecord } from '@mews/fp';
import { getClassificationsPrefix } from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import { useGuestNotesQuery } from '../hooks/useGuestNotesQuery';
import { AccountLinkNew } from '../../../../../../../components';
import { AccountsRecord } from '../../types';
import { NoteItem } from './NoteItem';

export type GuestNotesProps = AccountsNotesProps;

export const GuestNotes = ({ accountsRecord }: GuestNotesProps) => {
    const isEmpty = isEmptyRecord(accountsRecord);

    return (
        <Stack vertical spacing="200">
            <Stack spacing="100" itemAlignment="center">
                <Typography textStyle="bodyMediumStrong" variant="primary" as="div">
                    {lt('Guest')}
                </Typography>
                <TooltippedIcon
                    size="small"
                    value={IconName.Info}
                    tooltip={lt('GuestInHouseReportGuestNotesTooltip')}
                />
            </Stack>
            <Stack spacing="200" vertical>
                {isEmpty ? noData : <AccountsNotes accountsRecord={accountsRecord} />}
            </Stack>
        </Stack>
    );
};

const noData = <Empty textAlign="left" />;

interface AccountsNotesProps {
    accountsRecord: AccountsRecord;
}

const AccountsNotes = ({ accountsRecord }: AccountsNotesProps) => {
    const {
        data: { accountNotes, profiles },
        isFetching,
    } = useGuestNotesQuery({ accountIds: Object.keys(accountsRecord) });

    if (!accountNotes.length) return noData;

    return (
        <>
            <OverlayedLoader show={isFetching} />
            {accountNotes.map((note) => (
                <NoteItem
                    key={note.id}
                    tag={
                        <LocalizedEnum
                            prefix={getClassificationsPrefix(note)}
                            value={note.data.value.classifications[0]}
                        />
                    }
                    tagVariant="lavender"
                    text={note.content}
                    footer={
                        <Stack vertical>
                            <LocalizedText
                                textKey={TextKey.GuestInHouseReportGuestNotesLinkedToProfile}
                                substitutes={{
                                    accountName: accountsRecord[note.accountId] ? (
                                        <AccountLinkNew
                                            account={accountsRecord[note.accountId]}
                                            target="_blank"
                                            inherit
                                        />
                                    ) : (
                                        ''
                                    ),
                                }}
                            />
                            <Stack>
                                {note.createdUtc === note.updatedUtc ? lt('CreatedOn') : lt('UpdatedOn')}
                                {UnicodeEntity.NoBreakSpace}
                                <LocalizedDateTimeFromUtc
                                    utcString={note.createdUtc === note.updatedUtc ? note.createdUtc : note.updatedUtc}
                                />
                                {UnicodeEntity.NoBreakSpace}
                                {UnicodeEntity.LeftParenthesis}
                                {profiles?.[note.updaterProfileId]?.name}
                                {UnicodeEntity.RightParenthesis}
                            </Stack>
                        </Stack>
                    }
                />
            ))}
        </>
    );
};
